import {AfterViewInit, Component, Inject, Input, NgZone, OnChanges, OnDestroy, OnInit, PLATFORM_ID, SimpleChanges} from '@angular/core';
import {DataService} from '../../services/dataService/data.service';
import {IDiscoveryDates, IPriceDiscoveryModel} from '../../../models/IPriceDiscoveryModel';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5plugins_exporting from '@amcharts/amcharts5/plugins/exporting';
import {isPlatformBrowser} from '@angular/common';

@Component({
  selector: 'app-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  providers: [DataService]
})
export class DetailsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  @Input()
  harvestCode: string;
  @Input()
  id: string;
  @Input()
  isCollapsed: boolean;
  @Input()
  discoveryDates: IDiscoveryDates;
  @Input()
  commodityCode: string;
  @Input()
  stateCode: string;
  @Input()
  practiceName: string;
  @Input()
  harvestMonthName: string;
  @Input()
  typeName: string;
  @Input()
  priceFactor: number;
  @Input()
  year: number;
  @Input()
  actuarialDate: number;
  @Input()
  practiceCode: number;
  @Input()
  typeCode: number;
  @Input()
  harvestStarted: string;
  @Input()
  projectedStarted: string;

  // public chart: AmChart;
  public priceHistory: Array<any>;
  public testHistory: Array<IPriceDiscoveryModel>;
  public currentSelection: string;
  public harvestPrices: Array<any>;
  public projectedPrices: Array<any>;
  public chartData: Array<any>;
  public chartType: string;
  // private createdChart: AmChart;
  public chartMessage = 'LOADING ...';
  public historyTable: any;
  public chart: any;

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private zone: NgZone, private dataService: DataService) {
    this.chartData = [];
    this.harvestPrices = [];
    this.projectedPrices = [];
  }

  ngOnInit() {
    this.currentSelection = 'history';
  }

  async createChart(selection: string, isTable: boolean): Promise<void> {
    let root: am5.Root;
    if (root) {
      root.dispose();
    }


    const rootId = `${this.id}-${this.harvestCode}-${this.actuarialDate}-${this.practiceCode}-${this.currentSelection}`;

    if (selection === 'history') {
      this.chartType = 'line';
      root = am5.Root.new(rootId);
      root.setThemes([
        am5themes_Animated.new(root)
      ]);
      this.chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panY: false,
          panX: false,
          zoomX: true,
          zoomY: true,
          wheelX: 'zoomX',
          wheelY: 'zoomX',
          pinchZoomX: true,
          pinchZoomY: true,
          maxTooltipDistance: 4
        })
      );

      // Create Y-axis
      const yAxis = this.chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {}),
          tooltip: am5.Tooltip.new(root, {})
        })
      );

      // Create X-Axis
      const xAxis = this.chart.xAxes.push(
        am5xy.DateAxis.new(root, {
          maxDeviation: 1,
          baseInterval: {
            timeUnit: 'day',
            count: 1
          },
          markUnitChange: false,
          renderer: am5xy.AxisRendererX.new(root, {}),
          tooltip: am5.Tooltip.new(root, {}),
          tooltipDateFormat: 'MMM d',
          dateFormats: {day: 'MM/dd', month: 'MMM', year: 'MMM'}
        })
      );
      await this.chart.appear(250);

      // Create series
      for (const d of this.chartData) {
        const series = this.chart.series.push(am5xy.LineSeries.new(root, {
          name: d.cropYear,
          xAxis,
          yAxis,
          valueYField: 'close',
          valueXField: 'graphDate',
          legendLabelText: d.cropYear,
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: 'vertical',
            labelText: `[bold]${d.cropYear}[/]: {valueY}`
          })
        }));
        const data = d.dataPoints.map((p) => {
          const date = new Date(p.graphDate);
          date.setHours(0, 0, 0, 0);
          am5.time.add(date, 'day', 0);
          return {
            close: p.close,
            graphDate: date.getTime(),
            high: p.high,
            low: p.low,
            marketSymbol: p.marketSymbol,
            open: p.open,
            tradingDay: p.tradingDay,
            volume: p.volume
          };
        });
        series.strokes.template.setAll({
          strokeWidth: 3,
        });
        series.data.setAll(data);
        await series.appear(150, 100);
      }

      // Add legend
      this.addLegend(root);

      // Add cursor
      this.chart.set('cursor', am5xy.XYCursor.new(root, {
        behavior: 'zoomX'
      }));
    } else {
      root = am5.Root.new(rootId);
      root.setThemes([
        am5themes_Animated.new(root)
      ]);
      this.chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panY: true,
          panX: false,
          wheelX: 'none',
          wheelY: 'none',
          pinchZoomX: true,
          pinchZoomY: true,
          paddingBottom: 10,
          paddingTop: 10,
          paddingLeft: 10,
          paddingRight: 10
        })
      );

      this.chart.get('colors').set('step', 3);

      // Create Y-axis
      const yAxis = this.chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {})
        })
      );

      const yAxis2 = this.chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          syncWithAxis: yAxis,
          maxDeviation: 1,
          renderer: am5xy.AxisRendererY.new(root, {opposite: false})
        })
      );
      const yAxis3 = this.chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          syncWithAxis: yAxis,
          maxDeviation: 1,
          renderer: am5xy.AxisRendererY.new(root, {opposite: true})
        })
      );

      // Create X-Axis
      const xAxis = this.chart.xAxes.push(
        am5xy.DateAxis.new(root, {
          maxDeviation: 1,
          groupData: false,
          baseInterval: {
            timeUnit: 'day',
            count: 1
          },
          gridIntervals: [
            {timeUnit: 'millisecond', count: 1},
            {timeUnit: 'millisecond', count: 5},
            {timeUnit: 'millisecond', count: 10},
            {timeUnit: 'millisecond', count: 50},
            {timeUnit: 'millisecond', count: 100},
            {timeUnit: 'millisecond', count: 500},
            {timeUnit: 'second', count: 1},
            {timeUnit: 'second', count: 5},
            {timeUnit: 'second', count: 10},
            {timeUnit: 'second', count: 30},
            {timeUnit: 'minute', count: 1},
            {timeUnit: 'minute', count: 5},
            {timeUnit: 'minute', count: 10},
            {timeUnit: 'minute', count: 15},
            {timeUnit: 'minute', count: 30},
            {timeUnit: 'hour', count: 1},
            {timeUnit: 'hour', count: 3},
            {timeUnit: 'hour', count: 6},
            {timeUnit: 'hour', count: 12},
            {timeUnit: 'day', count: 1},
            {timeUnit: 'day', count: 2},
            {timeUnit: 'day', count: 3},
            {timeUnit: 'day', count: 4},
            {timeUnit: 'day', count: 5},
            {timeUnit: 'week', count: 1},
            {timeUnit: 'month', count: 1},
            {timeUnit: 'month', count: 2},
            {timeUnit: 'month', count: 3},
            {timeUnit: 'month', count: 6},
            {timeUnit: 'year', count: 1},
            {timeUnit: 'year', count: 2},
            {timeUnit: 'year', count: 5},
            {timeUnit: 'year', count: 10},
            {timeUnit: 'year', count: 50},
            {timeUnit: 'year', count: 100},
            {timeUnit: 'year', count: 200},
            {timeUnit: 'year', count: 500},
            {timeUnit: 'year', count: 1000},
            {timeUnit: 'year', count: 2000},
            {timeUnit: 'year', count: 5000},
            {timeUnit: 'year', count: 10000},
            {timeUnit: 'year', count: 100000}
          ],
          markUnitChange: false,
          renderer: am5xy.AxisRendererX.new(root, {}),
          tooltip: am5.Tooltip.new(root, {}),
          tooltipDateFormat: 'MMM d',
          dateFormats: {day: 'MM/dd', week: 'MM/dd', month: 'MM/dd', year: 'yyyy'}
        })
      );

      const data = this.historyTable.dataPoints.map((p) => {
        const date = new Date(p.date);
        date.setHours(0, 0, 0, 0);
        am5.time.add(date, 'day', 0);
        return {
          close: p.endingPrice,
          graphDate: date.getTime(),
          high: p.high,
          low: p.low,
          marketSymbol: p.marketSymbol,
          open: p.openingPrice,
          tradingDay: p.tradingDay,
          volume: p.volume,
          volatility: p.volatility,
          averageVolatility: p.fiveDayAverageVolatility,
          price: p.rmaPrice
        };
      });

      // Create series
      const endingPriceSeries = this.chart.series.push(am5xy.SmoothedXLineSeries.new(root, {
        name: 'endingPrice',
        xAxis,
        yAxis,
        valueYField: 'close',
        valueXField: 'graphDate',
        legendLabelText: 'Ending Price',
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: 'vertical',
          labelText: `[bold]Ending Price: [/]{valueY}`
        })
      }));
      endingPriceSeries.strokes.template.setAll({
        strokeWidth: 3,
      });
      endingPriceSeries.bullets.push(() =>
        am5.Bullet.new(root, {
          locationY: 1,
          locationX: undefined,
          sprite: am5.Circle.new(root, {
            radius: 5,
            fill: endingPriceSeries.get('fill')
          })
        })
      );

      const priceSeries = this.chart.series.push(am5xy.SmoothedXLineSeries.new(root, {
        name: 'price',
        xAxis,
        yAxis: yAxis2,
        valueYField: 'price',
        valueXField: 'graphDate',
        legendLabelText: 'Price',
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: 'vertical',
          labelText: `[bold]Price: [/]{valueY}`
        })
      }));
      priceSeries.strokes.template.setAll({
        strokeWidth: 3,
      });
      priceSeries.bullets.push(() =>
        am5.Bullet.new(root, {
          locationY: 1,
          locationX: undefined,
          sprite: am5.Circle.new(root, {
            radius: 5,
            fill: priceSeries.get('fill')
          })
        })
      );
      const volatilityAverageSeries = this.chart.series.push(am5xy.SmoothedXLineSeries.new(root, {
        name: 'price',
        xAxis,
        yAxis: yAxis3,
        valueYField: 'averageVolatility',
        valueXField: 'graphDate',
        legendLabelText: 'Volatility Avg.',
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: 'vertical',
          labelText: `[bold]Volatility Avg. [/]{valueY}`
        })
      }));
      volatilityAverageSeries.strokes.template.setAll({
        strokeWidth: 3,
        strokeDasharray: [10, 5, 2, 5]
      });
      volatilityAverageSeries.bullets.push(() =>
        am5.Bullet.new(root, {
          locationY: 1,
          locationX: undefined,
          sprite: am5.Circle.new(root, {
            radius: 5,
            fill: volatilityAverageSeries.get('fill')
          })
        })
      );
      const volatilitySeries = this.chart.series.push(am5xy.SmoothedXLineSeries.new(root, {
        name: 'price',
        xAxis,
        yAxis: yAxis3,
        valueYField: 'volatility',
        valueXField: 'graphDate',
        legendLabelText: 'Volatility',
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: 'vertical',
          labelText: `[bold]Volatility [/]{valueY}`
        })
      }));
      volatilitySeries.strokes.template.setAll({
        strokeWidth: 3,
      });
      volatilitySeries.bullets.push(() =>
        am5.Bullet.new(root, {
          locationY: 1,
          locationX: undefined,
          sprite: am5.Circle.new(root, {
            radius: 5,
            fill: volatilitySeries.get('fill')
          })
        })
      );
      await this.chart.appear();
      await priceSeries.appear(250, 100);
      await endingPriceSeries.appear(250, 200);
      await volatilityAverageSeries.appear(250, 400);
      await volatilitySeries.appear(250, 600);
      priceSeries.data.setAll(data);
      endingPriceSeries.data.setAll(data);
      volatilityAverageSeries.data.setAll(data);
      volatilitySeries.data.setAll(data);

      // Add legend
      this.addLegend(root);
      // Add cursor
      this.chart.set('cursor', am5xy.XYCursor.new(root, {}));
    }
    this.root = root;
  }

  private addLegend(root: am5.Root) {
    const legend = this.chart.topAxesContainer.children.push(am5.Legend.new(root, {
      paddingLeft: 15
    }));
    const chartRef = this.chart;
    legend.itemContainers.template.events.on('pointerover', function (e) {
      const itemContainer = e.target;

      // As series list is data of a legend, dataContext is series
      const series = itemContainer.dataItem.dataContext;

      chartRef.series.each((chartSeries) => {
        if (chartSeries !== series) {
          // @ts-ignore
          chartSeries.strokes.template.setAll({
            strokeOpacity: 0.15,
            stroke: am5.color(0x000000)
          });
        } else {
          // @ts-ignore
          chartSeries.strokes.template.setAll({
            strokeWidth: 5
          });
        }
      });
      am5plugins_exporting.Exporting.new(root, {
        menu: am5plugins_exporting.ExportingMenu.new(root, {}),
        filePrefix: 'FMH_Price_History'
      });
    });

    legend.itemContainers.template.events.on('pointerout', function (e) {
      const itemContainer = e.target;

      chartRef.series.each((chartSeries) => {
        // @ts-ignore
        chartSeries.strokes.template.setAll({
          strokeOpacity: 1,
          strokeWidth: 3,
          stroke: chartSeries.get('fill')
        });
      });
    });

    legend.data.setAll(this.chart.series.values);
  }

  public setChartData(selection) {
    this.root.dispose();
    this.root = null;
    this.currentSelection = selection;
    this.getTableData(selection);
  }

  // Run the function only in the browser
  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        f();
      });
    }
  }

  ngAfterViewInit() {
    // // Chart code goes in here
  }

  ngOnDestroy() {
    // Clean up chart when the component is removed
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
    });
  }

  getTableData(selection: string) {
    this.dataService.getTableData(
      this.actuarialDate,
      this.commodityCode,
      this.year,
      this.practiceCode,
      this.stateCode,
      this.typeCode,
      selection
    )
      .subscribe((res) => {
        this.historyTable = res;
        this.createChart(selection, true);
      });
  }

  // getYearData() {
  //   this.priceHistory = [];
  //   this.testHistory = [];
  //   let year = moment().year();
  //   const firstYear = moment().subtract(3, 'years').year();
  //   while (year >= firstYear) {
  //     this.dataService.getYearsHistorical({commodityCode: this.commodityCode, commodityYear: year}, this.stateCode)
  //       .subscribe((res) => {
  //         this.priceHistory.push(res);
  //         this.flattenArray(res);
  //       });
  //     year--;
  //   }
  // }

  retrieveHistoricalData(commodity: string, currentSelection: string) {
    this.dataService.getPriceHistory(commodity, this.year, this.stateCode)
      .subscribe(async (res: any) => {
        this.chartData = res.marketHistoryData;
        await this.createChart(currentSelection, false);
      });
  }

  /*

    zoomChart(time: string) {
      switch (time) {
        case 'harvest':
          this.zoomTo(new Date(this.discoveryDates.HarvestPriceBeginDate), new Date(this.discoveryDates.HarvestPriceEndDate));
          break;
        case 'projected':
          this.zoomTo(new Date(this.discoveryDates.ProjectedPriceBeginDate), new Date(this.discoveryDates.ProjectedPriceEndDate));
          break;
        case 'history':
          this.zoomOut();
          break;
      }
    }

    createSerialChart() {
      this.chart = {
        'type': 'serial',
        'pathToImages': '/FmhApps/amcharts/images/',
        'theme': 'light',
        'marginRight': 80,
        'dataProvider': this.chartData,
        'valueAxes': [{
          'position': 'left'
        }],
        'legend': {
          'enabled': true,
          'useGraphSettings': true
        },
        'graphs': [{
          'id': 'g1',
          'title': 'Open',
          'fillAlphas': 0,
          'lineThickness': 2,
          'bullet': 'round',
          'bulletSize': 8,
          'valueField': 'open',
          'fillColors': '#ADD8E6',
          'lineColor': '#ADD8E6',
          'balloonText': '<div style=\'margin:5px; font-size:19px;\'>Open:<b>[[value]]</b></div>'
        },
          {
            'id': 'g2',
            'title': 'High',
            'bullet': 'round',
            'bulletSize': 8,
            'fillAlphas': 0,
            'lineThickness': 2,
            'valueField': 'high',
            'lineColor': '#0B6623',
            'fillColors': '#0B6623',
            'balloonText': '<div style=\'margin:5px; font-size:19px;\'>High:<b>[[value]]</b></div>'
          },
          {
            'id': 'g3',
            'title': 'Low',
            'bullet': 'round',
            'bulletSize': 8,
            'fillAlphas': 0,
            'lineThickness': 2,
            'valueField': 'low',
            'lineColor': '#E51D1D',
            'fillColors': '#E51D1D',
            'balloonText': '<div style=\'margin:5px; font-size:19px;\'>Low:<b>[[value]]</b></div>'
          },
          {
            'id': 'g4',
            'title': 'Close',
            'fillAlphas': 0,
            'bullet': 'round',
            'bulletSize': 8,
            'lineThickness': 2,
            'valueField': 'close',
            'lineColor': '#FFA500',
            'fillColors': '#FFA500',
            'balloonText': '<div style=\'margin:5px; font-size:19px;\'>Close:<b>[[value]]</b></div>'
          }],
        'chartCursor': {
          'categoryBalloonDateFormat': 'DD MMMM',
          'cursorPosition': 'mouse',
          'position': 'bottom'
        },
        'categoryField': 'timestamp',
        'categoryAxis': {
          'minPeriod': 'mm',
          'parseDates': true
        }
      };
      this.createdChart = this.amChartsService.makeChart(this.retrieveChartId(), this.chart);
      this.zoomChart(this.currentSelection);
    }

    zoomTo(start: Date, end: Date) {
      switch (this.createdChart.type) {
        case 'serial':
          this.createdChart.zoomToDates(start, end);
          break;
        case 'stock':
          this.createdChart.zoom(start, end);
          break;
      }
    }

    zoomOut() {
      switch (this.createdChart.type) {
        case 'serial':
          this.createdChart.zoomOut();
          break;
        case 'stock':
          this.createdChart.zoomOut();
          break;
      }
    }

    createChart() {
      this.chart = {
        'type': 'stock',
        'pathToImages': '/FmhApps/amcharts/images/',
        'theme': 'light',
        'marginLeft': 10,
        'marginTop': 15,
        'dataSets': [{
          'fieldMappings': [{
            'fromField': 'open',
            'toField': 'open'
          }, {
            'fromField': 'close',
            'toField': 'close'
          }, {
            'fromField': 'high',
            'toField': 'high'
          }, {
            'fromField': 'low',
            'toField': 'low'
          }, {
            'fromField': 'volume',
            'toField': 'volume'
          }, {
            'fromField': 'value',
            'toField': 'value'
          }],
          'color': '#7f8da9',
          'dataProvider': this.chartData,
          'categoryField': 'timestamp'
        }],
        'balloon': {
          'horizontalPadding': 13
        },
        'panels': [{
          'title': 'Value',
          'stockGraphs': [{
            'id': 'g1',
            'type': 'candlestick',
            'openField': 'open',
            'closeField': 'close',
            'highField': 'high',
            'lowField': 'low',
            'valueField': 'close',
            'lineColor': '#7f8da9',
            'fillColors': '#7f8da9',
            'negativeLineColor': '#db4c3c',
            'negativeFillColors': '#db4c3c',
            'fillAlphas': 1,
            'balloonText': 'Open:<b>[[open]]</b><br>Close:<b>[[close]]</b><br>Low:<b>[[low]]</b><br>High:<b>[[high]]</b>',
            'useDataSetColors': false
          }]
        }],
        'scrollBarSettings': {
          'graphType': 'line',
          'usePeriod': 'WW'
        },
        'valueAxesSettings': {
          'inside': false
        },
        'panelsSettings': {
          'panEventsEnabled': true,
          'marginLeft': 40
        },
        'cursorSettings': {
          'valueBalloonsEnabled': true,
          'valueLineBalloonEnabled': true,
          'valueLineEnabled': true
        },
        'periodSelector': {
          'position': 'bottom',
          'periods': [{
            'period': 'DD',
            'count': 10,
            'label': '10 days'
          }, {
            'period': 'MM',
            'selected': true,
            'count': 1,
            'label': '1 month'
          }, {
            'period': 'YYYY',
            'count': 1,
            'label': '1 year'
          }, {
            'period': 'YTD',
            'label': 'YTD'
          }, {
            'period': 'MAX',
            'label': 'MAX'
          }]
        }
      };
      this.createdChart = this.amChartsService.makeChart(this.retrieveChartId(), this.chart);
    }

    changeChart(chartType: string) {
      if (this.chart.type !== chartType) {
        this.amChartsService.destroyChart(this.createdChart);
        this.chart = null;
        switch (chartType) {
          case 'serial':
            this.createSerialChart();
            break;
          case 'stock':
            this.createChart();
            break;
        }
      }
    }

    ngOnDestroy() {
      if (this.createdChart) {
        this.amChartsService.destroyChart(this.createdChart);
      }
      this.chart = null;
      this.chartData = null;
    }
  */

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges.isCollapsed) {
      if (!simpleChanges.isCollapsed.currentValue) {
        this.retrieveHistoricalData(this.harvestCode, this.currentSelection);
        this.getTableData('history');
      }
      if (simpleChanges.isCollapsed.currentValue) {
        if (this.root) {
          this.root.dispose();
        }
      }
    }
  }

}
